import { FormEvent, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

type RoleStatus = "Active" | "Limited" | "Suspended";

type Role = {
  id: string;
  name: string;
  members: number;
  permissions: string;
  status: RoleStatus;
};

const initialRoles: Role[] = [];

const statusTone: Record<RoleStatus, string> = {
  Active: "bg-emerald-500/15 text-emerald-100 border border-emerald-500/30",
  Limited: "bg-amber-500/15 text-amber-100 border border-amber-500/30",
  Suspended: "bg-rose-500/15 text-rose-100 border border-rose-500/30",
};

export function UserRolesAdmin() {
  const [roles, setRoles] = useState<Role[]>(initialRoles);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Role, "id">>({
    name: "",
    members: 1,
    permissions: "",
    status: "Active",
  });

  const isEditing = useMemo(() => Boolean(editingId), [editingId]);

  const resetForm = () => {
    setFormData({ name: "", members: 1, permissions: "", status: "Active" });
    setEditingId(null);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedId = (editingId ?? formData.name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    if (!normalizedId) return;

    setRoles(prev => {
      const nextRole: Role = { id: normalizedId, ...formData };

      if (editingId) {
        return prev.map(role => (role.id === editingId ? nextRole : role));
      }

      if (prev.some(role => role.id === normalizedId)) {
        return prev.map(role => (role.id === normalizedId ? nextRole : role));
      }

      return [...prev, nextRole];
    });

    resetForm();
  };

  const handleEdit = (role: Role) => {
    setFormData({ name: role.name, members: role.members, permissions: role.permissions, status: role.status });
    setEditingId(role.id);
  };

  return (
    <section className="rounded-3xl border border-border/60 bg-card/50 p-6 shadow-lg backdrop-blur">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">User roles</p>
          <h2 className="text-2xl font-display font-bold">Administration</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Add new user roles or modify the rows below to keep access aligned with your team’s needs.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className="rounded-full border-border/70 bg-muted/40">
            Real-time updates
          </Badge>
          <span className="hidden md:inline" aria-hidden>•</span>
          <span className="hidden md:inline">No refresh required</span>
        </div>
      </div>

      <div className="grid gap-6 pt-6 lg:grid-cols-[1.1fr_1fr]">
        <Card className="border border-border/60 bg-card/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-lg">{isEditing ? "Edit role" : "Add a new role"}</CardTitle>
            <CardDescription>
              Define the role name, limit the member count, and summarize the permissions users receive.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="role-name">Role name</Label>
                <Input
                  id="role-name"
                  value={formData.name}
                  onChange={event => setFormData(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="e.g. Content Strategist"
                  required
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="members">Members allowed</Label>
                  <Input
                    id="members"
                    type="number"
                    min={1}
                    value={formData.members}
                    onChange={event =>
                      setFormData(prev => ({ ...prev, members: Number(event.target.value) || 1 }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={value => setFormData(prev => ({ ...prev, status: value as RoleStatus }))}
                  >
                    <SelectTrigger id="status" className="bg-card/70">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-card/90">
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Limited">Limited</SelectItem>
                      <SelectItem value="Suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="permissions">Permissions overview</Label>
                <Input
                  id="permissions"
                  value={formData.permissions}
                  onChange={event => setFormData(prev => ({ ...prev, permissions: event.target.value }))}
                  placeholder="What can this role access?"
                  required
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="submit" className="flex-1 min-w-[140px]">
                  {isEditing ? "Save role" : "Add role"}
                </Button>
                {isEditing && (
                  <Button type="button" variant="ghost" className="flex-1 min-w-[120px]" onClick={resetForm}>
                    Cancel edit
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border border-border/60 bg-card/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-lg">Roles list</CardTitle>
            <CardDescription>Tap a row to modify it; changes save inline to the list.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-2xl border border-border/60">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-[120px]">Role</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead className="w-[120px]">Members</TableHead>
                    <TableHead className="w-[110px]">Status</TableHead>
                    <TableHead className="w-[70px] text-right">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                        No roles configured yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    roles.map(role => (
                      <TableRow
                        key={role.id}
                        className={cn(
                          "cursor-pointer transition hover:bg-muted/50",
                          editingId === role.id && "ring-1 ring-primary/40 bg-primary/5"
                        )}
                        onClick={() => handleEdit(role)}
                      >
                        <TableCell className="font-medium">{role.name}</TableCell>
                        <TableCell className="text-muted-foreground">{role.permissions}</TableCell>
                        <TableCell>{role.members}</TableCell>
                        <TableCell>
                          <Badge className={cn("rounded-full px-2.5 py-1 text-xs", statusTone[role.status])}>{role.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={event => { event.stopPropagation(); handleEdit(role); }}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
